import React, { useState, useMemo, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import {
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  ListingCard,
  NamedLink,
  Modal,
  ModalBlockedUser,
  Page,
  ProjectListingCard,
  EducationContainer,
  WorkExperienceWorkedWithContainer,
  FreelancerProfileUserCard,
  BuyerProfileUserCard,
  FreelancerAboutMeContainer,
  CustomerAboutMeContainer,
  ReviewsAndTestimonialsContainer,
  PortfolioWorksContainer,
} from '../../components';
import { EnquiryForm } from '../../forms';
import { NotFoundPage, TopbarContainer } from '../../containers';
import { sendProfileEnquiry } from './ProfilePage.duck';
import {
  buildProfileSchema,
  knowsAboutFromPublicData,
  serviceListingSchemaData,
  portfolioWorkSchemaData,
} from './profileSchema';
import config from '../../config';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { withViewport } from '../../util/contextHelpers';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { REVIEW_TYPE_OF_CUSTOMER, REVIEW_TYPE_OF_PROVIDER, propTypes } from '../../util/types';
import { createResourceLocatorString } from '../../util/routes';
import { richTextToMetaDescription } from '../../util/seo';
import { trackIntercomEvent } from '../../util/intercom';
import routeConfiguration from '../../routeConfiguration';
import { updateFavoriteProfiles } from '../../ducks/user.duck';

import { loadData } from './ProfilePage.duck';

import { FaPenAlt, FaHeart, FaRegHeart } from 'react-icons/fa';

import {
  uploadAttachment,
  removeAttachment,
  clearStagedAttachments,
  stagedAttachmentsList,
  readyAttachmentFileIds,
  SCOPE_PROFILE_ENQUIRY,
} from '../../ducks/messageAttachments.duck';
import css from './ProfilePage.css';

const { UUID } = sdkTypes;

const ProfilePageComponent = props => {
  const {
    scrollingDisabled,
    user,
    currentUser,
    userShowError,
    queryListingsError,
    listings,
    offPlatformTestimonials,
    skillOptions,
    toolOptions,
    onSendProfileEnquiry,
    onManageDisableScrolling,
    stagedAttachments,
    onUploadAttachment,
    onRemoveAttachment,
    onClearAttachments,
    intl,
    history,
    location,
    onUpdateFavoriteProfiles,
    favoriteProfiles,
    precomputedSchema,
    profileDataLoaded,
  } = props;

  const [enquiryModalOpen, setEnquiryModalOpen] = useState(false);
  const [blockedModalOpen, setBlockedModalOpen] = useState(false);

  const profileUser = ensureUser(user);
  const profileUserIdExists = profileUser && profileUser.id && profileUser.id.uuid;
  const hasBio = profileUser?.attributes?.profile?.publicData?.bio;

  // Scroll to the section named in the URL hash (e.g. /u/:id#services from the
  // service page; the ids mirror the ProfileSettingsPage sections). loadData
  // clears the page and loads its parts one after another, and later parts
  // (reviews, testimonials) appear above earlier ones, so wait until it has
  // finished. The first render can still hold the previous visit's data: only
  // a load that started after mounting counts. As on ProfileSettingsPage, each
  // history entry is handled once.
  const loadStartedRef = useRef(false);
  const handledHashLocationRef = useRef(null);
  useEffect(() => {
    if (!profileDataLoaded) {
      loadStartedRef.current = true;
      return;
    }
    if (!loadStartedRef.current || !location.hash) return;
    const locationKey = location.key || 'initial';
    if (handledHashLocationRef.current === locationKey) return;
    handledHashLocationRef.current = locationKey;

    const element = document.getElementById(location.hash.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [profileDataLoaded, location]);

  const profileListing = listings.filter(l => l?.attributes?.publicData?.listingType === 'profile');
  const profileListingId = profileListing.length > 0 ? profileListing[0].id.uuid : null;
  const profileUserRole = profileUser?.attributes?.profile?.publicData?.account_role;
  const isLightEntrepreneur = profileUserRole === 'light-entrepreneur';

  const combineReviews = () => {
    const { reviews, offPlatformReviews } = props;

    const formattedReviews = reviews.map(review => ({
      content: review?.attributes?.content,
      rating: review?.attributes?.rating,
      createdAt: review?.attributes?.createdAt,
      author: review?.author,
      authorName: review?.author?.attributes?.profile?.displayName,
      abbreviatedName: review?.author?.attributes?.profile?.abbreviatedName,
      type: review?.attributes?.type,
      id: review?.id?.uuid,
      onPlatform: true,
    }));

    const formattedOffPlatformReviews = offPlatformReviews.map(offReview => ({
      content: offReview?.content,
      rating: offReview?.reviewRating,
      createdAt: new Date(offReview?.createdAt),
      authorName: offReview?.displayName,
      abbreviatedName: offReview?.abbreviatedName,
      type: REVIEW_TYPE_OF_PROVIDER,
      id: offReview?._id,
      onPlatform: false,
    }));

    const allReviews = [...formattedReviews, ...formattedOffPlatformReviews];
    allReviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return allReviews;
  };

  const combinedReviews = combineReviews();

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const displayName = profileUser?.attributes?.profile?.displayName;

  const accountRole = profileUser?.attributes?.profile?.publicData?.account_role;
  const noIndexing = profileUser?.attributes?.profile?.publicData?.noIndexing || false;

  const showContainers = accountRole !== 'customer';

  const whoAmI = profileUser?.attributes?.profile?.publicData?.whoAmI;

  const reviewsOfProvider = combinedReviews?.filter(r => r?.type === REVIEW_TYPE_OF_PROVIDER);

  const reviewsOfCustomer = combinedReviews?.filter(r => r?.type === REVIEW_TYPE_OF_CUSTOMER);

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  const renderListing = l =>
    l.attributes?.publicData.listingType === 'service' && !l.attributes?.publicData.hidden ? (
      <li className={css.listing} key={l.id.uuid}>
        <ListingCard listing={l} />
      </li>
    ) : l.attributes?.publicData.listingType === 'project' ? (
      <li className={css.listing} key={l.id.uuid}>
        <ProjectListingCard listing={l} />
      </li>
    ) : null;

  const hasServiceListings = listings.find(
    l =>
      l?.attributes?.publicData?.listingType === 'service' &&
      !l?.attributes?.publicData?.hidden === true
  );

  const hasPortfolioWorks = listings.find(
    l => l?.attributes?.publicData?.listingType === 'portfolioWork'
  );

  const unsortedPortfolioWorks = listings.filter(
    l =>
      l?.attributes?.publicData?.listingType === 'portfolioWork' &&
      !l?.attributes?.publicData?.privateWork
  );

  // Sort portfolio works by featuredPortfolioOrder
  const featuredPortfolioOrder =
    profileUser?.attributes?.profile?.publicData?.featuredPortfolioOrder || [];

  const portfolioWorks = useMemo(() => {
    if (featuredPortfolioOrder.length === 0) {
      return unsortedPortfolioWorks;
    }

    const featuredWorks = [];
    const remainingWorks = [];

    unsortedPortfolioWorks.forEach(work => {
      const workId = work?.id?.uuid || work?.id;
      const orderEntry = featuredPortfolioOrder.find(entry => entry.id === workId);
      if (orderEntry) {
        featuredWorks.push({ work, order: orderEntry.order });
      } else {
        remainingWorks.push(work);
      }
    });

    // Sort featured works by order
    featuredWorks.sort((a, b) => a.order - b.order);
    return [...featuredWorks.map(fw => fw.work), ...remainingWorks];
  }, [unsortedPortfolioWorks, featuredPortfolioOrder]);

  const handleProfileEnquiry = (
    profileListingId,
    isLightEntrepreneur,
    message,
    currentUser,
    whoAmI
  ) => {
    const routes = routeConfiguration();

    onSendProfileEnquiry(
      profileListingId,
      isLightEntrepreneur,
      message,
      currentUser,
      whoAmI,
      readyAttachmentFileIds(stagedAttachments)
    )
      .then(response => {
        onClearAttachments();
        setEnquiryModalOpen(false);

        if (typeof window === 'object') {
          window.dataLayer.push({
            event: 'contact_author',
            content_type: 'freelancer',
            ecommerce: {
              items: [
                {
                  item_name: profileUser.attributes.profile.displayName,
                  item_id: profileUser.id.uuid,
                  quantity: '1',
                },
              ],
            },
          });
        }
        const transaction = response.data.data;
        const txId = transaction.id;

        history.push(
          createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {})
        );
      })
      .catch(error => {
        console.error('Failed to send profile enquiry:', error);
        // You might want to show an error message to the user here
        setEnquiryModalOpen(false);
      });
  };

  const isFavoriteProfile =
    favoriteProfiles && favoriteProfiles.some(x => x === profileUser?.id?.uuid);

  const handleAddRemoveFavorite = () => {
    if (!currentUser) {
      history.push('/signup');
      return;
    } else {
      const favoriteProfilesUuids = favoriteProfiles.filter(
        favoriteProfile => favoriteProfile !== profileUser.id.uuid
      );

      if (isFavoriteProfile) {
        onUpdateFavoriteProfiles(favoriteProfilesUuids);
      } else {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'add_to_favorites',
          content_type: 'freelancer',
          ecommerce: {
            items: [
              {
                item_id: profileUser.id.uuid,
                item_name: profileUser.attributes.profile.displayName,
                quantity: '1',
                item_list_name: 'search_results',
              },
            ],
          },
        });
        if (typeof window === 'object') {
          trackIntercomEvent('addedUserToFavorites', {
            userId: profileUser.id?.uuid,
            displayName: profileUser.attributes?.profile?.displayName,
          });
        }
        onUpdateFavoriteProfiles([...favoriteProfilesUuids, profileUser?.id?.uuid]);
      }
    }
  };

  const isUserBlocked =
    ensuredCurrentUser?.attributes?.profile?.metadata?.spamEmailDetected === true;

  const handleClickContactButton = () => {
    if (!currentUser) {
      history.push('/signup');
    } else if (isUserBlocked) {
      setBlockedModalOpen(true);
    } else {
      setEnquiryModalOpen(true);
      onManageDisableScrolling('ProfilePageEnquiryModal', true);
    }
  };

  const currentUserAccountRole = ensuredCurrentUser?.attributes?.profile?.publicData?.account_role;
  const contactButton =
    !currentUser || currentUserAccountRole === 'customer' ? (
      <div className={css.contactButtonContainer}>
        <div className={css.contactButton} onClick={handleClickContactButton}>
          <FormattedMessage id="ProfilePage.contactButton" />
        </div>
        <div className={css.addToFavorites} onClick={handleAddRemoveFavorite}>
          {!isFavoriteProfile ? (
            <FaRegHeart className={css.addToFavoritesIcon} />
          ) : (
            <FaHeart className={css.addToFavoritesIcon} />
          )}
        </div>
      </div>
    ) : null;

  const asideContent =
    profileUserIdExists && showContainers ? (
      <div className={css.asideContent}>
        <FreelancerProfileUserCard
          profileUser={profileUser}
          combinedReviews={reviewsOfProvider}
          offPlatformTestimonials={offPlatformTestimonials}
          contactButton={contactButton}
        />
      </div>
    ) : profileUserIdExists && !showContainers ? (
      <div className={css.asideContent}>
        <BuyerProfileUserCard profileUser={profileUser} combinedReviews={reviewsOfCustomer} />
      </div>
    ) : null;

  const workExperience = profileUser?.attributes?.profile?.publicData?.workExperience;
  const workedWith = profileUser?.attributes?.profile?.publicData?.workedWith;

  const mainContent =
    profileUserIdExists && showContainers ? (
      <div className={css.mainContent}>
        <FreelancerAboutMeContainer
          profileUser={profileUser}
          skillOptions={skillOptions}
          toolOptions={toolOptions}
        />
        {hasPortfolioWorks && portfolioWorks.length > 0 && (
          <PortfolioWorksContainer portfolioWorks={portfolioWorks} />
        )}

        {((workExperience && workExperience.length > 0) ||
          (workedWith && workedWith.length > 0)) && (
          <WorkExperienceWorkedWithContainer
            profileUser={profileUser}
            workExperience={workExperience}
            workedWith={workedWith}
          />
        )}
        <ReviewsAndTestimonialsContainer
          reviews={reviewsOfProvider}
          testimonials={offPlatformTestimonials}
        />
        <EducationContainer profileUser={profileUser} />
        {hasServiceListings ? (
          <div id="services" className={listingsContainerClasses}>
            <h2 className={css.listingsTitle}>
              <FormattedMessage
                id="ProfilePage.listingsTitle"
                values={{ user: profileUser.attributes?.profile?.displayName }}
              />
            </h2>
            <ul className={css.listings}>{listings.map(l => renderListing(l))}</ul>
          </div>
        ) : null}
      </div>
    ) : (
      <div className={css.mainContent}>
        <CustomerAboutMeContainer profileUser={profileUser} />
        <ReviewsAndTestimonialsContainer reviews={reviewsOfCustomer} testimonials={[]} />
      </div>
    );

  const schemaTitle =
    accountRole === 'customer'
      ? displayName
      : intl.formatMessage(
          {
            id: 'ProfilePage.schemaTitle',
          },
          {
            name: displayName,
            whoAmI: whoAmI,
          }
        );

  // The rich-text bio the profile actually renders lives in publicData. Unlike profile.bio
  // (which sanitizeUser rewrites <> to fullwidth ＜＞), its HTML tags can still be stripped.
  const metaDescription = richTextToMetaDescription(
    profileUser?.attributes?.profile?.publicData?.bio
  );
  const profileUrl = profileUserIdExists
    ? `${process.env.REACT_APP_CANONICAL_ROOT_URL}/u/${profileUser.id.uuid}`
    : undefined;

  const locationCity = profileUser?.attributes?.profile?.publicData?.locationCity;
  const locationCountryCode = profileUser?.attributes?.profile?.publicData?.locationCountryCode;

  // Use precomputed schema from SSR if available, otherwise compute client-side
  const schema = useMemo(() => {
    // If we have a precomputed schema from server-side rendering, use it
    if (precomputedSchema) {
      return precomputedSchema;
    }

    if (!profileUrl) {
      return null;
    }

    // Fallback to client-side schema generation (for client-side navigation)
    const canonicalRootURL = process.env.REACT_APP_CANONICAL_ROOT_URL;

    const services = listings
      .filter(
        l =>
          l?.attributes?.publicData?.listingType === 'service' && !l?.attributes?.publicData?.hidden
      )
      .map(listing => serviceListingSchemaData(listing, canonicalRootURL));

    const works = portfolioWorks.map(work => {
      const variants = work.images?.[0]?.attributes?.variants;
      const imageUrl = variants?.['landscape-crop2x']?.url || variants?.horizontal?.url;
      return portfolioWorkSchemaData(work, canonicalRootURL, imageUrl);
    });

    return buildProfileSchema({
      profileUrl,
      schemaTitle,
      displayName,
      whoAmI,
      knowsAbout: knowsAboutFromPublicData(
        profileUser?.attributes?.profile?.publicData,
        skillOptions,
        toolOptions,
        config.locale
      ),
      // Same rule as loadData in ProfilePage.duck.js: the schema description comes
      // from the stripped publicData bio, not the sanitized profile.bio.
      bio: metaDescription,
      locationCity,
      locationCountryCode,
      profileImageUrl: profileUser?.profileImage?.attributes?.variants?.['square-small2x']?.url,
      createdAt: profileUser?.attributes?.createdAt,
      reviews: combinedReviews || [],
      services,
      works,
    });
  }, [
    precomputedSchema,
    profileUrl,
    schemaTitle,
    displayName,
    whoAmI,
    metaDescription,
    locationCity,
    locationCountryCode,
    combinedReviews,
    listings,
    portfolioWorks,
    profileUser,
    skillOptions,
    toolOptions,
  ]);

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage history={''} location={''} />;
  } else if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      description={metaDescription}
      schema={schema}
      noIndexing={noIndexing || accountRole === 'customer'}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="ProfilePage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.main}>
          {isCurrentUser ? (
            <div className={css.modifyServiceHeading}>
              <h3 className={css.modifyServiceTitle}>
                <FormattedMessage id="ProfilePage.modifyServiceTitle" />
              </h3>
              <div className={css.editProfileLinkDesktop}>
                <FaPenAlt />
                <NamedLink name="ProfileSettingsPage">
                  <h3>
                    <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
                  </h3>
                </NamedLink>
              </div>
            </div>
          ) : null}
          <div className={css.mainContainer}>
            <div className={css.aside}>{profileUser && asideContent}</div>
            {profileUser && mainContent}
          </div>
          <Modal
            id="ProfilePageEnquiryModal"
            isOpen={enquiryModalOpen}
            onClose={() => {
              onClearAttachments();
              setEnquiryModalOpen(false);
            }}
            onManageDisableScrolling={onManageDisableScrolling}
            containerClassName={css.enquiryModalContainer}
            usePortal
          >
            <EnquiryForm
              className={css.enquiryForm}
              inProgress={false}
              listingTitle={whoAmI || ''}
              authorDisplayName={displayName || ''}
              author={profileUser}
              sendEnquiryError={null}
              stagedAttachments={stagedAttachments}
              onUploadAttachment={onUploadAttachment}
              onRemoveAttachment={onRemoveAttachment}
              onSubmit={values => {
                const message = values.message;
                handleProfileEnquiry(
                  profileListingId,
                  isLightEntrepreneur,
                  message,
                  currentUser,
                  whoAmI,
                  profileUser
                );
              }}
            />
          </Modal>
          <ModalBlockedUser
            isOpen={blockedModalOpen}
            onClose={() => setBlockedModalOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
          />
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

const { bool, arrayOf, number, shape, func } = PropTypes;

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  queryReviewsError: propTypes.error,
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
  intl: intlShape.isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  stagedAttachments: PropTypes.array.isRequired,
  onUploadAttachment: func.isRequired,
  onRemoveAttachment: func.isRequired,
  onClearAttachments: func.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
    offPlatformTestimonials,
    queryOffPlatformTestimonialsError,
    offPlatformReviews,
    queryOffPlatformReviewsError,
    profileSchema,
    favoriteProfiles: profilePageFavorites,
    profileDataLoaded,
  } = state.ProfilePage;
  const { skillOptions, toolOptions } = state.SkillsAndTools;

  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  const listings = getMarketplaceEntities(state, userListingRefs);

  // Use ProfilePage favoriteProfiles for SSR (preloaded), fallback to user.favoriteProfilesIds for client updates
  const favoriteProfiles =
    profilePageFavorites.length > 0 ? profilePageFavorites : state.user.favoriteProfilesIds || [];

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    favoriteProfiles,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
    offPlatformTestimonials,
    queryOffPlatformTestimonialsError,
    offPlatformReviews,
    queryOffPlatformReviewsError,
    skillOptions,
    toolOptions,
    precomputedSchema: profileSchema,
    profileDataLoaded,
    stagedAttachments: stagedAttachmentsList(state, SCOPE_PROFILE_ENQUIRY),
  };
};

const mapDispatchToProps = dispatch => ({
  onSendProfileEnquiry: (
    profileListingId,
    isLightEntrepreneur,
    message,
    currentUser,
    whoAmI,
    fileIds
  ) =>
    dispatch(
      sendProfileEnquiry(
        profileListingId,
        isLightEntrepreneur,
        message,
        currentUser,
        whoAmI,
        fileIds
      )
    ),
  onUploadAttachment: file => dispatch(uploadAttachment(SCOPE_PROFILE_ENQUIRY, file)),
  onRemoveAttachment: tempId => dispatch(removeAttachment(SCOPE_PROFILE_ENQUIRY, tempId)),
  onClearAttachments: () => dispatch(clearStagedAttachments(SCOPE_PROFILE_ENQUIRY)),
  onUpdateFavoriteProfiles: favoriteProfilesUuids =>
    dispatch(updateFavoriteProfiles(favoriteProfilesUuids)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ProfilePage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withViewport,
  injectIntl
)(ProfilePageComponent);

ProfilePage.loadData = params => {
  const id = new UUID(params.id);
  return loadData(id);
};

export default ProfilePage;
